import { useCallback, useState } from 'react';

import { type ErrorLike, type OperationVariables } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { Stack, useTheme } from '@mui/material';

import {
  type GetListingBuyersLeadQuery,
  type Property_Searches_Bool_Exp,
} from '@graphql/__generated__/graphql';
import { INSERT_BUYER_LEADS_MANY } from '@modules/_shared/components/buyer-lead/buyerLeadQueries';
import { buildOriginationActivity } from '@modules/_shared/components/buyer-lead/buyerLeadUtils';
import { PropertySearchesMatching } from '@modules/_shared/components/buyer-lead/PropertySearchesMatching';
import { Drawer } from '@modules/_shared/components/drawer/Drawer';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { emitFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import { UPDATE_AUTO_MATCHING_FILTERS } from '@modules/listings/lotsQueries';
import { useLocale } from '@shared/hooks/locale';

export const BuyerLeadMatchingDrawer: React.FC<{
  onClose: () => void;
  listing: GetListingBuyersLeadQuery['lots_by_pk'];
  destinationStageId?: string;
  refetch: (variables?: Partial<OperationVariables>) => Promise<unknown>;
}> = ({ onClose, listing, refetch, destinationStageId }) => {
  const { t } = useLocale();
  const { breakpoints } = useTheme();
  const { me } = useAppData();
  const [mutationError, setMutationError] = useState<ErrorLike | null>(null);

  const [insertBuyerLeads, { loading }] = useMutation(INSERT_BUYER_LEADS_MANY);
  const [
    updateAutoMatchingFilters,
    { loading: updateAutoMatchingFiltersLoading },
  ] = useMutation(UPDATE_AUTO_MATCHING_FILTERS);

  const handleAddBuyers = useCallback(
    (ids: string[]) => {
      if (listing == null || ids.length === 0) {
        return;
      }

      setMutationError(null);
      insertBuyerLeads({
        variables: {
          buyer_leads: ids.map(id => ({
            user_id: id,
            lot_id: listing.id,
            source: 'matching',
            stage_id: destinationStageId,
            activities: buildOriginationActivity(
              id,
              me?.is_admin ? me?.id : undefined,
            ),
          })),
        },
        onCompleted: data => {
          if (data.insert_buyer_leads != null) {
            onClose();
            refetch();
            emitFeedRefresh();
          }
        },
        onError: setMutationError,
      });
    },
    [
      listing,
      insertBuyerLeads,
      destinationStageId,
      me?.is_admin,
      me?.id,
      onClose,
      refetch,
    ],
  );

  const handleSaveCriteria = useCallback(
    (criteria: Property_Searches_Bool_Exp | null) => {
      if (listing == null) {
        return;
      }

      updateAutoMatchingFilters({
        variables: {
          id: listing.id,
          filters: criteria,
        },
        onError: setMutationError,
      });
    },
    [updateAutoMatchingFilters, listing],
  );

  if (listing == null) {
    return null;
  }

  return (
    <Drawer
      onClose={onClose}
      title={t('Buyer leads matching')}
      maxWidth={breakpoints.values.lg}
    >
      <Stack sx={{ height: '100%' }}>
        <PropertySearchesMatching
          defaultFilters={{
            user: {
              id: {
                _nin: (listing.all_buyer_leads ?? []).map(
                  buyerLead => buyerLead.user_id,
                ),
              },
            },
            offer_type:
              listing.offer_type != null
                ? {
                    _in: [listing.offer_type === 'sell' ? 'buy' : 'rent'],
                  }
                : undefined,
          }}
          onAdd={handleAddBuyers}
          onCancel={onClose}
          isAdding={loading}
          onSaveCriteria={handleSaveCriteria}
          isSavingCriteria={updateAutoMatchingFiltersLoading}
          initialCriteria={listing.auto_matching_filters}
        />
      </Stack>
      <MutationErrorSnackbar
        error={mutationError}
        onClose={() => setMutationError(null)}
      />
    </Drawer>
  );
};
